import { Injectable } from '@angular/core';
import { AlertController, AlertOptions, ModalController, ModalOptions } from '@ionic/angular';
import { Observable, Subject, Subscription } from 'rxjs';
import { concatMap, delay, first, map, switchMap, tap } from 'rxjs/operators';
import { FeatureFlagFacade } from 'src/app/core/state/facades/feature-flag.facade';
import { ModalMatDialog } from 'src/app/shared/models/modal-mat-dialog.model';

@Injectable({
  providedIn: 'root',
})
export class ModalDialogService {
  // For storing the queue of dialogues to be presented to end-user.
  private dialogQueueSubj: Subject<ModalMatDialog> = new Subject();

  // For storing queue subscription so it can be unsubscribed later.
  private dialogQueueSubs: Subscription;

  constructor(
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private featureFlagFacade: FeatureFlagFacade,
  ) {
    this.dialogQueueSubs = this.queueImplementation(this.dialogQueueSubj);
  }

  /**
   * Implements the queue of the dialogues using RxJs flattening operators approach.
   *
   * @param subject - Expects Subject<IMatDialog> so it can run pipe on it.
   * @returns Subscription so it can be unsubscribed later on.
   */
  private queueImplementation(subject: Subject<ModalMatDialog>): Subscription {
    // The way it works is that addDialogToQueue function sends dialog configurations to dialogQueueSubj subject.
    // Here we convert subject to observable, pipe it and then use concatMap to dequeue dialogues by fifo principle.
    // We wait for each dialog to emit the result after being closed, this result is then sent back to the dialog
    // creator so it can subscribe to it. Additionally, we display next dialog within 500ms delay so it looks nicer.
    return subject
      .asObservable()
      .pipe(
        concatMap((queueItem: ModalMatDialog) =>
          this.openDialog$(queueItem.options).pipe(
            tap((result: any) => {
              queueItem.result.next(result);
              queueItem.result.complete();
            }),
            delay(500), // So we give it a bit of time before we show the next modal dialog from the queue for the better user experience.
          ),
        ),
      )
      .subscribe();
  }

  /**
   * Custom type checking guard to detect if provided options are of type ModalOptions and contain feature flags.
   *
   * @param options - takes modal dialog options.
   */
  private instanceOfModalOptionsWithFeatureFlags(options: ModalOptions | AlertOptions): options is ModalOptions {
    return (options as ModalOptions)?.componentProps?.resolveFeatureFlags?.featureKeys !== undefined;
  }

  /**
   * Custom type checking guard to detect if an objects is of a type ModalOptions.
   *
   * @param options - takes modal dialog options.
   * @returns true if object is of a ModalOptions type.
   */
  private instanceOfModalOptions(options: ModalOptions | AlertOptions): options is ModalOptions {
    return 'component' in options;
  }

  /**
   *  Helper function opens modal dialog and provides result as observable.
   *
   * @param options - takes modal dialog options.
   */
  private modalDialog$(options: ModalOptions): Observable<any> {
    return new Observable((subscriber) => {
      this.modalCtrl.create(options).then((modal) => {
        // https://github.com/ionic-team/ionic-framework/issues/26872
        modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

        modal.onDidDismiss().then((response) => {
          subscriber.next(response);
          subscriber.complete();
        });
        modal.present();
      });
    });
  }

  /**
   * Helper function opens alert dialog and provides result as observable.
   *
   * @param options - takes modal dialog options.
   */
  private alertDialog$(options: AlertOptions): Observable<any> {
    return new Observable((subscriber) => {
      this.alertCtrl.create(options).then((modal) => {
        modal.onDidDismiss().then((response) => {
          subscriber.next(response);
          subscriber.complete();
        });
        modal.present();
      });
    });
  }

  /**
   * Opens modal or alert window and returns observable, which resolves after it is closed.
   * This observable is used to return the result of the dialog in the code invoking it.
   *
   * @param options - ModalOptions or AlertOptions which dialog is created with.
   * @returns observable with the results of the user interaction with the modal.
   */
  private openDialog$(options: ModalOptions | AlertOptions): Observable<any> {
    // This block helps to achieve a similar behaviour to route resolver.
    //
    // If resolveFeatureFlags object is provided when dialog is created or added to the queue
    // the feature flags will then be resolved first and the result will be passed to
    // the modal dialog component options as featureFlags object in componentProps so
    // it can then be used in the dialog itself without ever querying feature flags separately.
    if (this.instanceOfModalOptionsWithFeatureFlags(options)) {
      const { featureKeys, attributes } = options.componentProps.resolveFeatureFlags;
      return this.featureFlagFacade.isFeatureEnabled$(featureKeys, attributes).pipe(
        map((featureFlags) => ({ ...options, componentProps: { ...options.componentProps, featureFlags } })),
        switchMap((optionsWithFeatureFlags) => this.modalDialog$(optionsWithFeatureFlags)),
      );
    }

    if (this.instanceOfModalOptions(options)) {
      return this.modalDialog$(options);
    }

    return this.alertDialog$(options);
  }

  /**
   * Adds dialog to queue of modal dialogues.
   *
   * @param options - ModalOptions or AlertOptions which dialog is created with.
   * @param config - Expects object of MatDialogConfig.
   * @returns - Result of the modal dialog interaction.
   */
  addDialogToQueue(options: ModalOptions | AlertOptions): Subject<any> {
    const result = new Subject();
    const dialog: ModalMatDialog = {
      options,
      result,
    };
    this.dialogQueueSubj.next(dialog);
    return result;
  }

  /**
   * Presents dialog immediately and resolves feature flags if resolveFeatureFlags is present in componentProps.
   *
   * @param options - Expects Component or Template reference.
   */
  openDialog(options: ModalOptions | AlertOptions): Subject<any> {
    const resultSubj = new Subject();
    this.openDialog$(options)
      .pipe(
        first(),
        tap((result: any) => {
          resultSubj.next(result);
          resultSubj.complete();
        }),
        delay(500), // So we give it a bit of time before we show the next modal dialog from the queue for the better user experience.
      )
      .subscribe();
    return resultSubj;
  }

  /**
   * Empties the queue of dialogues.
   */
  emptyDialogQueue(): void {
    this.dialogQueueSubs.unsubscribe();
    this.dialogQueueSubs = this.queueImplementation(this.dialogQueueSubj);
  }

  /**
   * Empties the queue of dialogues and dismisses those already displayed.
   */
  dismissAll(): void {
    this.emptyDialogQueue();
    this.modalCtrl.dismiss();
    this.alertCtrl.dismiss();
  }
}
